import { Category, PlantProfile } from "protos/robot_syncer/plant_profile";
import { CategoryCollectionProfile } from "protos/veselka/prediction_point";
import { DateRange } from "@mui/x-date-pickers-pro";
import { DateTime } from "luxon";
import {
  Entity,
  NumericalRangeValue,
} from "portal/components/filters/filter.types";
import { isEmpty } from "common/utils/arrays";
import { Optional } from "common/utils/types";
import {
  PageSortParams,
  PredictionPointBaseFilters,
  PredictionPointSessionFilters,
} from "protos/portal/veselka";

/**
 * API filter shape that allows missing filters to be undefined as opposed to an empty array
 * to satisfy our FE useQuery checks that yell at us if we pass empty arrays as args
 * (proto types restrict us to defining repeated as a required array)
 */
export type PredictionPointApiBaseFilters = Optional<
  PredictionPointBaseFilters,
  "ids" | "cropIds" | "carbonCropIds" | "robotSerials" | "customerIds"
>;

type PredictionPointApiSessionFilters = Optional<
  PredictionPointSessionFilters,
  "categoryIds"
>;

/** merged proto defs to comprise full API proto filter input */
export type PredictionPointFilters = PageSortParams &
  PredictionPointApiBaseFilters &
  PredictionPointApiSessionFilters;

/** Crop filter option. Carbon-crop mode marks the selected ID as a Carbon crop. */
export interface UiCropFilterValue extends Entity {
  isCarbonCrop?: true;
}

/** UI version that allows us to use fully described values like Entity[] and DateRange for pickers */
export interface UiPredictionPointBaseFilters {
  crops: UiCropFilterValue[];
  robots: Entity[];
  capturedAt: DateRange<DateTime> | undefined;
  radiusUserUnits: NumericalRangeValue | undefined;
  uploadedByOperator: boolean | undefined;
  plantProfileCategoryId: Entity | undefined; // plant-profile category id used to expand chip IDs server-side
}
export interface UiPredictionPointSessionFilters {
  sessionId: string | undefined;
  categoryIds: Entity[];
  /** Veselka `crop_ids` frozen at preview-session create. */
  sessionCropIds?: string[];
}
export type UiPredictionPointFilters = UiPredictionPointBaseFilters &
  UiPredictionPointSessionFilters;

export const toPredictionPointBaseFilters = (
  uiFilters: Omit<UiPredictionPointFilters, "radiusUserUnits">,
  radiusMin?: number,
  radiusMax?: number,
  customerId?: string
): PredictionPointApiBaseFilters => {
  const cropIds = uiFilters.crops
    .filter((crop) => !crop.isCarbonCrop)
    .map((crop) => crop.id);
  const carbonCropIds = uiFilters.crops
    .filter((crop) => crop.isCarbonCrop)
    .map((crop) => crop.id);
  const robotSerials = uiFilters.robots.map((r) => r.id);
  return {
    cropIds: isEmpty(cropIds) ? undefined : cropIds,
    carbonCropIds: isEmpty(carbonCropIds) ? undefined : carbonCropIds,
    robotSerials: isEmpty(robotSerials) ? undefined : robotSerials,
    capturedAtStart: uiFilters.capturedAt?.[0]?.toISO() ?? undefined,
    capturedAtEnd: uiFilters.capturedAt?.[1]?.toISO() ?? undefined,
    radiusMin,
    radiusMax,
    uploadedByOperator: uiFilters.uploadedByOperator,
    customerIds: customerId ? [customerId] : undefined,
    ppCategoryId: uiFilters.plantProfileCategoryId?.id,
  };
};

// UI filters -> proto
export const toPredictionPointFilters = (
  pagination: PageSortParams,
  uiFilters: Omit<UiPredictionPointFilters, "radiusUserUnits">,
  radiusMin?: number,
  radiusMax?: number,
  customerId?: string
): PredictionPointFilters => {
  const categoryIds = uiFilters.categoryIds.map((c) => c.id);
  const baseFilters = toPredictionPointBaseFilters(
    uiFilters,
    radiusMin,
    radiusMax,
    customerId
  );
  if (uiFilters.sessionId && uiFilters.sessionCropIds !== undefined) {
    baseFilters.cropIds = isEmpty(uiFilters.sessionCropIds)
      ? undefined
      : uiFilters.sessionCropIds;
  }
  return {
    ...pagination,
    ...baseFilters,
    sessionId: uiFilters.sessionId,
    categoryIds: isEmpty(categoryIds) ? undefined : categoryIds,
  };
};

export const toApiCategoryCollectionProfile = (
  pp: Pick<PlantProfile, "id"> & {
    categories: Pick<Category, "id" | "chipIds">[];
  }
): CategoryCollectionProfile => ({
  id: pp.id,
  categoryProfiles: pp.categories.map(({ id, chipIds }) => ({
    id,
    predictionPointIds: chipIds,
  })),
});
